import { useState, type FormEvent } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Eye, EyeOff, Loader2, ShieldCheck } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormField } from '@/components/FormField'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { ApiError } from '@/lib/api'
import { tokenManager } from '@/lib/tokenManager'
import { authApi } from '@/features/auth/api'
import type { CognitoSigninResponse } from '@/features/auth/types'

const errMsg = (
  err: unknown,
  fallback = "We couldn't sign you in. Please try again in a moment.",
) => {
  if (err instanceof ApiError && err.body && typeof err.body === 'object' && 'error' in err.body) {
    const m = (err.body as { error: unknown }).error
    if (typeof m === 'string' && m) return m
  }
  if (err instanceof Error && err.message) return err.message
  return fallback
}

export function AdminLoginPage() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [showPwd, setShowPwd] = useState(false)
  const [step, setStep] = useState<'password' | 'totp'>('password')
  const [totp, setTotp] = useState('')
  const [pending, setPending] = useState<CognitoSigninResponse | null>(null)
  const [localMode, setLocalMode] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const completeFromAppJwt = (session: { token?: string; userId?: number | string; name?: string; email?: string; error?: string }) => {
    if (!session.token) throw new Error(session.error || 'Session exchange failed.')
    tokenManager.set(session.token)
    if (session.userId) localStorage.setItem('userId', String(session.userId))
    if (session.name) localStorage.setItem('userName', session.name)
    if (session.email) localStorage.setItem('userEmail', session.email)
    sessionStorage.setItem('bk.is_staff', '1')
    navigate('/platform-admin', { replace: true })
  }

  const completeSignin = async (data: CognitoSigninResponse) => {
    if (!data.idToken) {
      throw new Error("We couldn't read your sign-in details. Please try signing in again.")
    }
    const session = await authApi.adminCognitoSession(data.idToken)
    completeFromAppJwt(session)
  }

  const handleResult = async (data: CognitoSigninResponse) => {
    if (data.step === 'done') {
      await completeSignin(data)
      return
    }
    if (data.step === 'totp') {
      setPending(data)
      setStep('totp')
      return
    }
    setError(
      "Your sign-in needs an extra step we don't support yet. Please contact a Bokapsys super admin.",
    )
  }

  const submitPassword = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      try {
        const data = await authApi.adminCognitoSignin({ email, password })
        setLocalMode(false)
        await handleResult(data)
      } catch (err) {
        if (err instanceof ApiError && err.status === 503) {
          // Local-dev fallback: Cognito unset, BFF exposes a bcrypt+TOTP
          // path that mirrors the customer LoginPage 503 → /api/auth/login.
          // When LOCAL_STAFF_TOTP_SKIP is on, the BFF returns step:'done'
          // with the App JWT directly - no second round-trip needed.
          const data = await authApi.adminLocalSignin({ email, password })
          setLocalMode(true)
          if (data.step === 'done' && data.token) {
            completeFromAppJwt(data)
            return
          }
          if (data.step === 'totp') {
            setPending(data)
            setStep('totp')
            return
          }
          setError(
            "Your sign-in needs an extra step we don't support yet. Please contact a Bokapsys super admin.",
          )
          return
        }
        throw err
      }
    } catch (err) {
      setError(errMsg(err))
    } finally {
      setBusy(false)
    }
  }

  const submitTotp = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault()
    setError(null)
    if (!/^\d{6}$/.test(totp)) {
      setError('Please enter the 6-digit code from your authenticator app.')
      return
    }
    if (!pending?.session) {
      setError('Your sign-in attempt timed out. Please re-enter your password to start again.')
      setStep('password')
      return
    }
    setBusy(true)
    try {
      if (localMode) {
        const data = await authApi.adminLocalTotp({ session: pending.session, code: totp })
        if (data.step === 'done' && data.token) {
          completeFromAppJwt(data)
          return
        }
        setError(
          data.error ||
            "We couldn't verify that code. Please try the new 6-digit code from your authenticator app.",
        )
        return
      }
      const data = await authApi.adminCognitoConfirm({
        email,
        session: pending.session,
        challengeName: 'SOFTWARE_TOKEN_MFA',
        responses: { SOFTWARE_TOKEN_MFA_CODE: totp },
      })
      // If MFA fails, Cognito invalidates the session - surface a restart hint
      await handleResult(data)
    } catch (err) {
      // Backend signals MFA_RESTART / DEVICE_TRUST_RESET when the only
      // way forward is to redo the password step. Surface its message
      // and bounce the user back so they get a fresh challenge.
      if (
        err instanceof ApiError &&
        err.body &&
        typeof err.body === 'object' &&
        'code' in err.body &&
        ((err.body as { code: unknown }).code === 'MFA_RESTART' ||
          (err.body as { code: unknown }).code === 'DEVICE_TRUST_RESET')
      ) {
        setError(errMsg(err))
        setStep('password')
        setPending(null)
        setTotp('')
        return
      }
      setError(
        errMsg(
          err,
          "We couldn't verify that code. Please try the new 6-digit code from your authenticator app.",
        ),
      )
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-center text-2xl">Bokapsys staff sign in</CardTitle>
        <p className="text-center text-sm text-muted-foreground">
          Two-factor authentication is required.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {error ? (
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        ) : null}

        {step === 'password' ? (
          <form onSubmit={submitPassword} className="space-y-3">
            <FormField
              label="Staff email"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.currentTarget.value)}
              autoComplete="email"
            />
            <FormField
              label="Password"
              control={
                <div className="relative">
                  <Input
                    type={showPwd ? 'text' : 'password'}
                    required
                    value={password}
                    onChange={(e) => setPassword(e.currentTarget.value)}
                    autoComplete="current-password"
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    onClick={() => setShowPwd((v) => !v)}
                    className="absolute right-1 top-1/2 size-8 -translate-y-1/2"
                    aria-label={showPwd ? 'Hide password' : 'Show password'}
                  >
                    {showPwd ? <EyeOff className="size-4" /> : <Eye className="size-4" />}
                  </Button>
                </div>
              }
            />
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : <ShieldCheck className="size-4" />}
              Continue
            </Button>
            <p className="text-center text-sm">
              <Link to="/forgot-password?staff=1" className="text-primary hover:underline">
                Forgot password?
              </Link>
            </p>
            <p className="text-center text-xs text-muted-foreground">
              Customer?{' '}
              <Link to="/login" className="text-primary hover:underline">
                Sign in here
              </Link>
            </p>
          </form>
        ) : (
          <form onSubmit={submitTotp} className="space-y-3">
            <p className="text-sm text-muted-foreground">
              Enter the 6-digit code from your authenticator app for {email}.
            </p>
            <FormField
              label="Authenticator code"
              value={totp}
              onChange={(e) => setTotp(e.currentTarget.value.replace(/\D/g, '').slice(0, 6))}
              maxLength={6}
              inputMode="numeric"
              className="text-center font-mono text-xl tracking-[10px]"
              autoFocus
            />
            <Button type="submit" className="w-full" disabled={busy}>
              {busy ? <Loader2 className="size-4 animate-spin" /> : null}
              Verify and sign in
            </Button>
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              onClick={() => {
                setStep('password')
                setPending(null)
                setTotp('')
              }}
            >
              ← Use a different account
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  )
}
